import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarClock } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { SnapshotEventsView } from "@/modules/snapshots/snapshotEvents/snapshotEvents.view";
import { useNavigate, useParams } from "react-router";
import React from "react";
import { SidePanel } from "@/components/chat/SidePanel";
import { SnapshotEventAnalytics } from "@/modules/snapshots/snapshotEvents/snapshotEventsAnalytics";
import { SnapshotEventsProvider } from "@/modules/snapshots/snapshotEvents/snapshotEvents.context";
import { useSnapshotEventsContext } from "@/modules/snapshots/snapshotEvents/useSnapshotEventsContext";
import { SnapshotEventCard } from "@/modules/snapshots/snapshotEvents/components/SnapshotEventCard";
import { ViewSnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/viewSnapshotEventDialog";
import { ModifySnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/modifySnapshotEventDialog";
import { clsx } from "clsx";

const Component = () => {
  const { tab = "eventAnalytics" } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = React.useState(tab);

  const {
    setSelectedEvent,
    setSelectedEventToEdit,
    setSelectedSnapshotEventGroupIds,
    selectedEvent,
    selectedEventToEdit,
    selectedSnapshotEventGroups,
  } = useSnapshotEventsContext();

  const handleChangeTab = (value: string) => {
    setSelectedTab(value);

    navigate(`/snapshot-events/${value}`);
  };

  React.useEffect(() => {
    setSelectedTab(tab);
  }, [setSelectedTab, tab]);

  return (
    <div className="relative -mx-4 -my-8 h-screen overflow-hidden sm:-mx-6 lg:-mx-8">
      <div
        className={clsx({
          // make backdrop clickable and above content but below the panel
          "fixed inset-0 z-20 cursor-pointer bg-black/50 opacity-100 transition-opacity duration-300":
            selectedSnapshotEventGroups !== null,
        })}
        onClick={() => setSelectedSnapshotEventGroupIds(null)}
        aria-hidden
      ></div>
      <div className="mx-4 my-8 h-full w-full overflow-auto px-4 sm:mx-6 lg:mx-8">
        <Tabs className="w-full" defaultValue={tab} onValueChange={handleChangeTab}>
          <TabsList className="flex justify-start bg-transparent px-2">
            <TabsTrigger
              value="snapshotEvents"
              className="group flex items-center border-b-2 border-transparent px-4 py-4 text-sm font-medium text-gray-500 hover:border-desert-sand-300 hover:text-gray-700 data-[state=active]:border-desert-sand-700 data-[state=active]:bg-ui-app-background data-[state=active]:text-desert-sand-700"
            >
              <CalendarClock className="mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-data-[state=active]:text-desert-sand-700" />
              <span className="px-1">Snapshot Events</span>
            </TabsTrigger>

            <TabsTrigger
              value="eventAnalytics"
              className="group flex items-center border-b-2 border-transparent px-4 py-4 text-sm font-medium text-gray-500 hover:border-desert-sand-300 hover:text-gray-700 data-[state=active]:border-desert-sand-700 data-[state=active]:bg-ui-app-background data-[state=active]:text-desert-sand-700"
            >
              <CalendarClock className="mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-data-[state=active]:text-desert-sand-700" />
              <span className="px-1">Analytics</span>
            </TabsTrigger>
          </TabsList>
          <div className="mt-1">
            <Separator />
          </div>
        </Tabs>

        <div className="pr-8 pt-5">
          {selectedTab == "snapshotEvents" && (
            <div className="mt-5 rounded-xl bg-white p-5 shadow-md">
              <SnapshotEventsView />
            </div>
          )}

          {selectedTab == "eventAnalytics" && (
            <div>
              <SnapshotEventAnalytics />
            </div>
          )}
        </div>
      </div>
      <div className="absolute right-0 top-0 z-30 h-[100vh] bg-gray-50 bg-ui-app-background">
        <SidePanel
          activeTab="library"
          tabs={[]}
          isOpen={selectedSnapshotEventGroups !== null}
          onClose={() => setSelectedSnapshotEventGroupIds(null)}
          onTabChange={() => {}}
          widthClassName="w-[50vw]"
          hideTabs
        >
          <div className="max-h-[100vh] overflow-y-scroll bg-ui-app-background">
            <div className="flex flex-col gap-2 px-2 py-2">
              {selectedSnapshotEventGroups?.map(snapshotEvent => (
                <SnapshotEventCard key={snapshotEvent.id} event={snapshotEvent} />
              ))}
            </div>
          </div>
        </SidePanel>
      </div>

      {selectedEventToEdit && (
        <ModifySnapshotEventDialog
          open={selectedEventToEdit !== null}
          onClose={() => setSelectedEventToEdit(null)}
          snapshotEvent={selectedEventToEdit}
          businessReportOutcomeId={selectedEventToEdit.business_report_outcome_id as string}
          businessReportOutcomeResultId={selectedEventToEdit.business_report_outcome_result_id as string}
          mode="edit"
          eventType={selectedEventToEdit.event_type}
          eventDate={selectedEvent?.event_date ? new Date(selectedEventToEdit.event_date) : undefined}
        />
      )}
      {selectedEvent && (
        <ViewSnapshotEventDialog
          open={selectedEvent !== null}
          onClose={() => setSelectedEvent(null)}
          onEdit={ev => {
            setSelectedEvent(null);
            setSelectedEventToEdit(ev);
          }}
          snapshotEvent={selectedEvent}
        />
      )}
    </div>
  );
};

export const SnapshotEventsPage = () => (
  <SnapshotEventsProvider>
    <Component />
  </SnapshotEventsProvider>
);
